import { useRef, useState } from "react";
import { useOutletContext, useParams } from "react-router-dom";
import { MenuItem, MenuSelectEvent } from "@progress/kendo-react-layout";
import { GridColumn, GridCellProps } from "@progress/kendo-react-grid";
import { InspectionsPageContext } from "../InspectionsPage";
import { useSharedState } from "../../../app/sharedProperty";
import { OvalityDataPointViewModel, OvalityInsertModel } from "../../../data/entities";
import { ConfirmationModal, Icon, IconDefinitions, ODataGrid, SpinnerDefault, TitlelessCard } from "../../../components";
import { OvalityDataPointViewService } from "../../../services";
import EditOvalityDataPointViewModal from "./components/EditOvalityDataPointViewModal";
import { Button } from "react-bootstrap";
import { usePageActions } from "../..";
import InsertOvalityModal from "./components/InsertOvalityModal";
import OvalityForm from "./components/OvalityForm";

export function InspectionsDataOvality() {
  const context: InspectionsPageContext = useOutletContext();
  context.setTitle("Ovality");

  const { id } = useParams();
  const inspectionId = Number(id);
  const gridData = useSharedState<Array<OvalityDataPointViewModel>>([]);
  const gridRef = useRef<ODataGrid>(null);
  const [showSpinner, setShowSpinner] = useState(false);

  const editItemState = useSharedState<OvalityDataPointViewModel>({} as OvalityDataPointViewModel);
  const showEditState = useSharedState(false);

  const resetItemState = useSharedState<OvalityDataPointViewModel>({} as OvalityDataPointViewModel);
  const showResetState = useSharedState(false);

  const showInsertState = useSharedState(false);

  const pointService = new OvalityDataPointViewService();

  const getOvalityDataPointViews = async (oDataQuery: string) =>
    await pointService.getAll(inspectionId, oDataQuery);

  const onRowMenuSelect = async (e: MenuSelectEvent, dataItem: any) => {
    switch (e.item.data.action) {
      case "edit":
        showEdit(dataItem);
        break;
      case "reset":
        showReset(dataItem);
        break;
    }
  };

  const showEdit = (dataItem: OvalityDataPointViewModel) => {
    editItemState.setter(dataItem);
    showEditState.setter(true);
  };

  const showReset = (dataItem: OvalityDataPointViewModel) => {
    resetItemState.setter(dataItem);
    showResetState.setter(true);
  };

  const showInsert = () => {
    showInsertState.setter(true);
  };

  const update = async (newItem: OvalityDataPointViewModel) => {
    setShowSpinner(true);
    try {
      showEditState.setter(false);
      let updatedItem = await pointService.update(newItem);
      gridData.setter((c) => c.map(i => i.id === updatedItem.id ? updatedItem : i));
    } catch (err) {
      throw new Error(`Unable to edit data point: ${err}`);
    } finally {
      setShowSpinner(false);
    }
  };

  const reset = async (dataItem: OvalityDataPointViewModel) => {
    setShowSpinner(true);
    try {
      showEditState.setter(false);
      let resetItem = await pointService.reset(dataItem.id);
      gridData.setter((c) => c.map(i => i.id === resetItem.id ? resetItem : i));
    } catch (err) {
      throw new Error(`Unable to reset data point: ${err}`);
    } finally {
      setShowSpinner(false);
    }
  };

  const insert = async (data: OvalityInsertModel) => {
    try {
      showInsertState.setter(false);
      setShowSpinner(true);
      await pointService.insert(inspectionId, data);
      gridRef.current?.refreshData();
    } catch (err) {
      throw new Error(`Unable to insert new ovality data, ${err}`);
    } finally {
      setShowSpinner(false);
    }
  }

  const IndexCell = (props: GridCellProps) =>
    <td valign="middle" style={{ lineHeight: 1 }}>
      {props.dataItem.dataPointIndexByInspection}
      {!props.dataItem.normalizedDate ? <></> :
        <Icon onClick={() => showReset(props.dataItem)} iconName={IconDefinitions.normalize} className="float-right" />
      }
      {!props.dataItem.manuallyAdjustedDate ? <></> :
        <Icon onClick={() => showReset(props.dataItem)} iconName={IconDefinitions.edit} className="float-right" />
      }
    </td>;

  usePageActions(
    <Button onClick={showInsert}>Upload Ovality</Button>
  );

  return (
    <>
      <TitlelessCard className="p-0 m-0" bodyClassName="p-0">
        <SpinnerDefault show={showSpinner} fullscreen={true} />
        <ODataGrid
          ref={gridRef}
          fullHeight={true}
          getData={getOvalityDataPointViews}
          dataState={gridData}
          onRowMenuSelect={onRowMenuSelect}
          onRowClick={(e) => showEdit(e.dataItem)}
          sort={[{ field: "dataPointIndexByInspection", dir: "asc" }]}
          scrollable="scrollable"
          pageSize={100}
        >
          <GridColumn title="Index" field="dataPointIndexByInspection" cell={IndexCell} filter="numeric" />
          <GridColumn title="Pos" field="startPosition" filter="numeric" />
          <GridColumn title="OD Min" field="diameterMinimum" filter="numeric" format="{0:N6}" />
          <GridColumn title="OD Max" field="diameterMaximum" filter="numeric" format="{0:N6}" />
          <GridColumn title="OD Avg" field="diameterAverage" filter="numeric" format="{0:N6}" />
          <GridColumn title="Ovality" field="ovalityPercent" filter="numeric" format="{0:P4}" />
          <MenuItem text="Edit" data={{ action: "edit" }} icon={IconDefinitions.edit} />
          <MenuItem text="Reset" data={{ action: "reset" }} icon={IconDefinitions.refresh} />
        </ODataGrid>
      </TitlelessCard>
      <OvalityForm 
        showState={showEditState}
        dataItemState={editItemState}
        onSubmit={update}
      />
      <ConfirmationModal
        onConfirm={() => reset(resetItemState.value)}
        showState={showResetState}
        title={`Reset ovality #${resetItemState.value.dataPointIndexByInspection}`}
        message={`You are about to reset ovality point #${resetItemState.value.dataPointIndexByInspection} to its original values. Do you want to continue?`}
        confirmText="Reset"
      />
      <InsertOvalityModal
        showState={showInsertState}
        onSubmit={insert}
        inspectionId={inspectionId}
        size="lg"
      />
    </>
  );
}

export default InspectionsDataOvality;

